import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';

type Props = {
  mode?: 'boot' | 'login';
  ready?: boolean;
  onComplete?: () => void;
};

export function BrandSplash({ mode = 'boot', ready = true, onComplete }: Props) {
  const { t } = useI18n();
  const [progress, setProgress] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    let frame = 0;
    let leaveTimer: number | undefined;
    let finishTimer: number | undefined;
    const startedAt = performance.now();
    const minimumDuration = mode === 'boot' ? 1_450 : 1_050;
    const finishingDuration = 320;
    let finishingAt: number | undefined;

    const tick = (now: number) => {
      const elapsed = now - startedAt;
      const base = Math.min(92, (elapsed / minimumDuration) * 92);

      if (ready && elapsed >= minimumDuration && finishingAt === undefined) {
        finishingAt = now;
      }

      const next = finishingAt === undefined
        ? base
        : Math.min(100, 92 + ((now - finishingAt) / finishingDuration) * 8);
      setProgress(next);

      if (next >= 100 && !completedRef.current) {
        completedRef.current = true;
        leaveTimer = window.setTimeout(() => setLeaving(true), 110);
        finishTimer = window.setTimeout(() => onCompleteRef.current?.(), 520);
        return;
      }

      frame = window.requestAnimationFrame(tick);
    };

    frame = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frame);
      if (leaveTimer !== undefined) window.clearTimeout(leaveTimer);
      if (finishTimer !== undefined) window.clearTimeout(finishTimer);
    };
  }, [mode, ready]);

  const status = progress >= 99.5
    ? t('splash.ready')
    : mode === 'login'
      ? t('splash.loginSuccess')
      : t('splash.booting');

  return (
    <div
      className={`brand-splash ${leaving ? 'is-leaving' : 'is-entering'}`}
      role="status"
      aria-live="polite"
      aria-label={mode === 'login' ? t('splash.loginAria') : t('splash.bootAria')}
    >
      <div className="brand-splash-glow brand-splash-glow-one" />
      <div className="brand-splash-glow brand-splash-glow-two" />
      <div className="brand-splash-card">
        <div className="brand-splash-logo-shell">
          <img src="/oa-plaza-logo.png" alt={t('app.company')} />
        </div>
        <div className="brand-splash-company">{t('app.company')}</div>
        <h1>{t('app.fullName')}</h1>
        <p>{t('splash.subtitle')}</p>
        <div
          className="brand-splash-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <span style={{ width: `${progress}%` }} />
        </div>
        <small>{status}</small>
      </div>
    </div>
  );
}
